import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, X, Pencil, TrendingDown, Repeat, Coins, Filter,
  Zap, Wrench, Building2, Receipt, Layers, Users,
  Info, ChevronDown, ChevronRight,
} from "lucide-react";
import { fmtMoney, fmtDate } from "@/lib/format";

/* -------------------------------------------------------------------------
 * 5 gruppi principali (Excel tracker): Utenze, Attrezzatura & Prodotti,
 * Personale, Fornitori & Servizi, Fiscalità.
 * Le etichette sono chiavi i18n (costs.groups.*).
 * ---------------------------------------------------------------------- */

const GROUPS = [
  { key: "utenze", color: "#FBBF24", icon: Zap, subcats: ["luce", "gas", "acqua", "internet", "affitto", "condominio", "altro"] },
  { key: "attrezzatura", color: "#22D3EE", icon: Wrench, subcats: ["prodotti_tecnici", "monouso", "attrezzatura", "manutenzione", "altro"] },
  { key: "personale", color: "#4ADE80", icon: Users, subcats: ["stipendio", "contributi", "tfr", "premi", "buoni", "formazione", "altro"] },
  { key: "fornitori", color: "#A78BFA", icon: Building2, subcats: ["commercialista", "marketing", "software", "assicurazione", "consulenza", "altro"] },
  { key: "fiscalita", color: "#F87171", icon: Receipt, subcats: ["f24", "inps", "iva", "irpef", "altro"] },
];
const GROUP_BY_KEY = Object.fromEntries(GROUPS.map((g) => [g.key, g]));

// Mapping category (backend field) → group key (front-end)
const CATEGORY_TO_GROUP = {
  utenze: "utenze", utilities: "utenze", rent: "utenze",
  attrezzatura: "attrezzatura", products: "attrezzatura",
  personale: "personale", staff: "personale",
  fornitori: "fornitori", marketing: "fornitori",
  fiscalita: "fiscalita", taxes: "fiscalita",
  other: null,
};

const PERIOD_KEYS = ["month", "year", "all", "custom"];

function ymd(d) { return d.toISOString().slice(0, 10); }
function computePeriod(kind, custom) {
  const now = new Date();
  if (kind === "month") return { from: ymd(new Date(now.getFullYear(), now.getMonth(), 1)), to: ymd(new Date(now.getFullYear(), now.getMonth() + 1, 0)) };
  if (kind === "year") return { from: `${now.getFullYear()}-01-01`, to: `${now.getFullYear()}-12-31` };
  if (kind === "custom") return custom;
  return { from: null, to: null };
}

// -----------------------------------------------------------------------
// Donut SVG puro: mostra la composizione dei gruppi in un colpo d'occhio
// -----------------------------------------------------------------------
function Donut({ segments, total, size = 260 }) {
  const { t } = useTranslation();
  const stroke = 26;
  const r = size / 2 - stroke / 2 - 2;
  const cx = size / 2, cy = size / 2;
  const C = 2 * Math.PI * r;
  let off = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="mx-auto">
      <circle cx={cx} cy={cy} r={r} stroke="var(--sp-subtle)" strokeWidth={stroke} fill="none" />
      {total > 0 && segments.map((s, i) => {
        const len = (s.value / total) * C;
        const el = (
          <circle key={i} cx={cx} cy={cy} r={r} stroke={s.color} strokeWidth={stroke} fill="none"
                  strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-off}
                  transform={`rotate(-90 ${cx} ${cy})`} style={{ transition: "all 500ms" }} />
        );
        off += len;
        return el;
      })}
      <text x={cx} y={cy - 6} textAnchor="middle" className="fill-[color:var(--sp-text)]"
            style={{ fontWeight: 900, fontSize: 30, fontFamily: "var(--sp-font-display)" }}>
        {fmtMoney(total)}
      </text>
      <text x={cx} y={cy + 18} textAnchor="middle" className="fill-[color:var(--sp-text-muted)]"
            style={{ fontSize: 11, letterSpacing: 1.4, textTransform: "uppercase", fontWeight: 700 }}>
        {t("costs.totalPeriod")}
      </text>
    </svg>
  );
}

export default function Costs() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [periodKind, setPeriodKind] = useState("month");
  const [customRange, setCustomRange] = useState({
    from: ymd(new Date(new Date().getFullYear(), 0, 1)),
    to: ymd(new Date()),
  });
  const [activeGroup, setActiveGroup] = useState("utenze");
  const [expandedGroup, setExpandedGroup] = useState(null); // per drill-down list

  function emptyForm() {
    return {
      name: "", category: "utenze", subcategory: "",
      amount: 0, date: new Date().toISOString().slice(0, 10),
      recurring: false, frequency: "monthly", notes: "", staff_id: "",
    };
  }

  const groupLabel = (key) => t(`costs.groups.${key}.label`);
  const period = useMemo(() => computePeriod(periodKind, customRange), [periodKind, customRange]);

  const load = async () => {
    const params = new URLSearchParams();
    if (period.from) params.set("date_from", period.from);
    if (period.to) params.set("date_to", period.to);
    const qs = params.toString();
    const [c, s, st] = await Promise.all([
      api.get("/costs"),
      api.get(`/costs/summary${qs ? `?${qs}` : ""}`),
      api.get("/staff").catch(() => ({ data: [] })),
    ]);
    setItems(c.data);
    setSummary(s.data);
    setStaffList(st.data || []);
  };
  useEffect(() => { load(); }, [periodKind, customRange.from, customRange.to]); // eslint-disable-line

  const openForm = (c = null, presetGroup = null) => {
    if (c) {
      setEditing(c.id);
      setForm({
        name: c.name, category: c.category, subcategory: c.subcategory || "",
        amount: c.amount, date: c.date, recurring: c.recurring,
        frequency: c.frequency || "monthly", notes: c.notes || "",
        staff_id: c.staff_id || "",
      });
    } else {
      setEditing(null);
      setForm({ ...emptyForm(), category: presetGroup || activeGroup });
    }
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        amount: Number(form.amount),
        frequency: form.recurring ? form.frequency : null,
        notes: form.notes || null,
        subcategory: form.subcategory || null,
        staff_id: form.category === "personale" && form.staff_id ? form.staff_id : null,
      };
      if (editing) await api.patch(`/costs/${editing}`, payload);
      else await api.post("/costs", payload);
      toast.success(editing ? t("costs.updated") : t("costs.added"));
      setShowForm(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || t("common.error")); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("costs.deleteConfirm"))) return;
    await api.delete(`/costs/${id}`);
    load();
  };

  // ---- Derived data ------------------------------------------------------
  const byGroup = useMemo(() => summary?.by_group || {}, [summary]);
  const bySubcategory = useMemo(() => summary?.by_subcategory || {}, [summary]);
  const inventoryValue = summary?.inventory_value ?? 0;

  const totalManual = useMemo(
    () => (byGroup.utenze || 0) + (byGroup.attrezzatura || 0) + (byGroup.personale || 0) + (byGroup.fornitori || 0) + (byGroup.fiscalita || 0),
    [byGroup]
  );

  const donutSegments = useMemo(
    () => GROUPS.map((g) => ({ label: groupLabel(g.key), value: byGroup[g.key] || 0, color: g.color })),
    [byGroup, t] // eslint-disable-line
  );

  const itemsInPeriod = useMemo(() => {
    if (!period.from && !period.to) return items;
    return items.filter((c) => {
      if (!c.date) return false;
      if (period.from && c.date < period.from) return false;
      if (period.to && c.date > period.to) return false;
      return true;
    });
  }, [items, period.from, period.to]);

  const itemsByGroup = useMemo(() => {
    const m = { utenze: [], attrezzatura: [], personale: [], fornitori: [], fiscalita: [], other: [] };
    for (const c of itemsInPeriod) {
      const g = CATEGORY_TO_GROUP[c.category] || "other";
      m[g].push(c);
    }
    for (const g of Object.keys(m)) m[g].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    return m;
  }, [itemsInPeriod]);

  const periodLabel = useMemo(() => {
    if (periodKind === "custom") return t("costs.periodLabels.custom", { from: fmtDate(period.from), to: fmtDate(period.to) });
    return t(`costs.periodLabels.${periodKind}`);
  }, [periodKind, period.from, period.to, t]);

  // Suggerimenti per popolare la sottocategoria in fase di modifica
  const activeGroupObj = GROUP_BY_KEY[form.category] || GROUP_BY_KEY.utenze;

  return (
    <div data-testid="costs-page" className="space-y-8">
      {/* Header */}
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-4xl font-black tracking-tight inline-flex items-center gap-3">
            <TrendingDown className="h-7 w-7 text-[color:var(--sp-brand)]" />
            {t("costs.title")}
          </h1>
          <p className="text-[color:var(--sp-text-secondary)] mt-2">
            {t("costs.subtitle")}
          </p>
        </div>
        <button data-testid="costs-new-btn" onClick={() => openForm()} className="sp-btn-primary inline-flex items-center gap-2">
          <Plus className="h-4 w-4" /> {t("costs.add")}
        </button>
      </header>

      {/* Period filter */}
      <div className="sp-card p-4 flex items-center gap-3 flex-wrap">
        <div className="inline-flex items-center gap-2 text-xs text-[color:var(--sp-text-muted)]">
          <Filter className="h-3.5 w-3.5" />
          <span className="font-bold uppercase tracking-widest">{t("costs.period")}</span>
        </div>
        <div className="inline-flex rounded-full border border-[color:var(--sp-border)] bg-white p-1">
          {PERIOD_KEYS.map((k) => (
            <button key={k} data-testid={`cost-period-${k}`} onClick={() => setPeriodKind(k)}
                    className={`px-3 py-1 rounded-full text-xs font-bold transition ${periodKind === k ? "bg-[color:var(--sp-brand)] text-white" : "text-[color:var(--sp-text-secondary)] hover:bg-[color:var(--sp-subtle)]"}`}>
              {t(`costs.periods.${k}`)}
            </button>
          ))}
        </div>
        {periodKind === "custom" && (
          <div className="inline-flex items-center gap-2">
            <input data-testid="cost-period-from" type="date" value={customRange.from} onChange={(e) => setCustomRange({ ...customRange, from: e.target.value })} className="sp-input !py-1 text-xs w-auto" />
            <span className="text-xs">→</span>
            <input data-testid="cost-period-to" type="date" value={customRange.to} onChange={(e) => setCustomRange({ ...customRange, to: e.target.value })} className="sp-input !py-1 text-xs w-auto" />
          </div>
        )}
        <div className="ml-auto text-xs text-[color:var(--sp-text-muted)]">{periodLabel}</div>
      </div>

      {/* Overview: donut + KPI colonna */}
      <section className="grid lg:grid-cols-5 gap-5">
        <div className="sp-card p-6 lg:col-span-2 flex flex-col items-center justify-center">
          <Donut segments={donutSegments} total={totalManual} />
          <div className="mt-5 grid grid-cols-1 gap-1.5 w-full max-w-[280px]">
            {GROUPS.map((g) => {
              const val = byGroup[g.key] || 0;
              const pct = totalManual ? (val / totalManual) * 100 : 0;
              return (
                <div key={g.key} className="flex items-center gap-2 text-xs">
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: g.color }} />
                  <span className="font-bold truncate">{groupLabel(g.key)}</span>
                  <span className="ml-auto tabular-nums font-black" style={{ color: g.color }}>{fmtMoney(val)}</span>
                  <span className="tabular-nums text-[10px] text-[color:var(--sp-text-muted)] w-9 text-right">{pct.toFixed(0)}%</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sp-card p-5 sp-anim">
            <div className="flex items-center justify-between">
              <span className="label-eyebrow">{t("costs.kpi.periodCosts")}</span>
              <TrendingDown className="h-4 w-4 text-[color:var(--sp-danger)]" />
            </div>
            <div className="font-display text-3xl font-black mt-3">{fmtMoney(summary?.total_period ?? 0)}</div>
            <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-1">{periodLabel}</div>
          </div>
          <div className="sp-card p-5 sp-anim">
            <div className="flex items-center justify-between">
              <span className="label-eyebrow">{t("costs.kpi.currentYear")}</span>
              <Coins className="h-4 w-4 text-[color:var(--sp-brand)]" />
            </div>
            <div className="font-display text-3xl font-black mt-3">{fmtMoney(summary?.total_year ?? 0)}</div>
          </div>
          <div className="sp-card p-5 sp-anim">
            <div className="flex items-center justify-between">
              <span className="label-eyebrow">{t("costs.kpi.recurring")}</span>
              <Repeat className="h-4 w-4 text-[color:var(--sp-brand)]" />
            </div>
            <div className="font-display text-3xl font-black mt-3">{summary?.recurring_count ?? 0}</div>
            <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-1">{t("costs.kpi.recurringRecords")}</div>
          </div>
          <div className="sp-card p-5 sp-anim bg-gradient-to-br from-cyan-50 to-white border-cyan-200">
            <div className="flex items-center justify-between">
              <span className="label-eyebrow inline-flex items-center gap-1">
                <Layers className="h-3 w-3" /> {t("costs.kpi.inventory")}
              </span>
              <Info className="h-3.5 w-3.5 text-[color:var(--sp-text-muted)]" />
            </div>
            <div className="font-display text-3xl font-black mt-3 text-cyan-700">{fmtMoney(inventoryValue)}</div>
            <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-1">
              {t("costs.kpi.inventoryHint")}
            </div>
          </div>
        </div>
      </section>

      {/* Category cards (interactive tabs) */}
      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {GROUPS.map((g) => {
          const total = byGroup[g.key] || 0;
          const share = totalManual ? (total / totalManual) * 100 : 0;
          const isActive = activeGroup === g.key;
          const Icon = g.icon;
          return (
            <button
              key={g.key}
              data-testid={`cost-group-${g.key}`}
              onClick={() => setActiveGroup(g.key)}
              className={`sp-card p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-lg ${isActive ? "ring-2" : ""}`}
              style={isActive ? { "--tw-ring-color": g.color, boxShadow: `0 0 0 2px ${g.color}, 0 8px 24px -12px ${g.color}` } : {}}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                     style={{ background: `${g.color}18`, color: g.color }}>
                  <Icon className="h-5 w-5" />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--sp-text-muted)]">
                  {share.toFixed(0)}%
                </span>
              </div>
              <div className="mt-4">
                <div className="text-xs font-bold uppercase tracking-widest text-[color:var(--sp-text-muted)]">{groupLabel(g.key)}</div>
                <div className="font-display text-2xl font-black mt-1">{fmtMoney(total)}</div>
              </div>
              <div className="h-1.5 rounded-full bg-[color:var(--sp-subtle)] mt-4 overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${share}%`, background: g.color }} />
              </div>
            </button>
          );
        })}
      </section>

      {/* Detail panel for active group */}
      <ActiveGroupDetail
        group={GROUP_BY_KEY[activeGroup]}
        items={itemsByGroup[activeGroup] || []}
        subTotals={bySubcategory[activeGroup] || {}}
        summary={summary}
        onAdd={() => openForm(null, activeGroup)}
        onEdit={(c) => openForm(c)}
        onDelete={remove}
        expanded={expandedGroup === activeGroup}
        onToggleExpand={() => setExpandedGroup((v) => (v === activeGroup ? null : activeGroup))}
      />

      {/* Legacy "Altro" (bucket per categorie non mappate) */}
      {(itemsByGroup.other || []).length > 0 && (
        <div className="sp-card p-4 border-dashed">
          <div className="flex items-center justify-between">
            <div className="text-sm font-bold inline-flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-[color:var(--sp-text-muted)]" />
              {t("costs.otherBucket")}
            </div>
            <div className="text-sm font-black">{fmtMoney((itemsByGroup.other || []).reduce((a, b) => a + (b.amount || 0), 0))}</div>
          </div>
          <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-1">
            {t("costs.otherHint", { n: itemsByGroup.other.length })}
          </div>
        </div>
      )}

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 z-30 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowForm(false)}>
          <div className="sp-card w-full max-w-lg p-8 my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl font-black">{editing ? t("costs.form.editTitle") : t("costs.form.newTitle")}</h3>
              <button onClick={() => setShowForm(false)}><X className="h-4 w-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-sm font-bold">{t("costs.form.description")}</label>
                <input data-testid="cost-name-input" required placeholder={t("costs.form.descPlaceholder")} className="sp-input mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-bold">{t("costs.form.category")}</label>
                  <select data-testid="cost-category-select" className="sp-input mt-1" value={form.category}
                          onChange={(e) => setForm({ ...form, category: e.target.value, subcategory: "" })}>
                    {GROUPS.map((g) => <option key={g.key} value={g.key}>{groupLabel(g.key)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-bold">{t("costs.form.subcategory")}</label>
                  <select data-testid="cost-subcategory-select" className="sp-input mt-1" value={form.subcategory}
                          onChange={(e) => setForm({ ...form, subcategory: e.target.value })}>
                    <option value="">{t("costs.form.noSubcategory")}</option>
                    {(activeGroupObj.subcats || []).map((s) => (
                      <option key={s} value={s}>{t(`costs.groups.${activeGroupObj.key}.subcats.${s}`)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-bold">{t("costs.form.amount")}</label>
                  <input data-testid="cost-amount-input" required type="number" step="0.01" min="0" className="sp-input mt-1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm font-bold">{t("costs.form.date")}</label>
                  <input data-testid="cost-date-input" type="date" required className="sp-input mt-1" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                </div>
              </div>
              {form.category === "personale" && (
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                  <label className="text-sm font-bold inline-flex items-center gap-2">
                    <Users className="h-3.5 w-3.5 text-emerald-600" />
                    {t("costs.form.collaborator")}
                    <span className="text-[10px] font-normal text-[color:var(--sp-text-muted)]">{t("costs.form.collaboratorHint")}</span>
                  </label>
                  <select data-testid="cost-staff-select" className="sp-input mt-1"
                          value={form.staff_id} onChange={(e) => setForm({ ...form, staff_id: e.target.value })}>
                    <option value="">{t("costs.form.unassigned")}</option>
                    {staffList.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              )}
              <div className="p-3 rounded-lg border border-[color:var(--sp-border)]">
                <label className="text-sm font-bold flex items-center gap-2">
                  <input type="checkbox" checked={form.recurring} onChange={(e) => setForm({ ...form, recurring: e.target.checked })} />
                  {t("costs.form.recurring")}
                </label>
                {form.recurring && (
                  <div className="mt-3">
                    <label className="text-xs font-bold">{t("costs.form.frequency")}</label>
                    <select className="sp-input mt-1" value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}>
                      <option value="monthly">{t("costs.form.monthly")}</option>
                      <option value="yearly">{t("costs.form.yearly")}</option>
                    </select>
                  </div>
                )}
              </div>
              <div>
                <label className="text-sm font-bold">{t("costs.form.notes")}</label>
                <input data-testid="cost-notes-input" placeholder={t("costs.form.notesPlaceholder")} className="sp-input mt-1" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <button data-testid="cost-submit-btn" className="sp-btn-primary w-full">
                {editing ? t("costs.form.saveChanges") : t("costs.add")}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// Detail panel per gruppo attivo — sottocategorie + lista voci
// -----------------------------------------------------------------------
function ActiveGroupDetail({ group, items, subTotals, summary, onAdd, onEdit, onDelete, expanded, onToggleExpand }) {
  const { t } = useTranslation();
  if (!group) return null;
  const Icon = group.icon;
  const total = Object.values(subTotals || {}).reduce((a, b) => a + b, 0);
  const subEntries = Object.entries(subTotals || {}).sort((a, b) => b[1] - a[1]);
  const subLabel = (val) => {
    if (!val) return val;
    const owner = GROUPS.find((g) => (g.subcats || []).includes(val));
    return owner ? t(`costs.groups.${owner.key}.subcats.${val}`) : val;
  };

  return (
    <section className="sp-card overflow-hidden" data-testid={`cost-detail-${group.key}`}>
      <header className="p-6 flex items-start justify-between gap-4 flex-wrap"
              style={{ background: `linear-gradient(135deg, ${group.color}10, transparent)` }}>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
               style={{ background: `${group.color}20`, color: group.color }}>
            <Icon className="h-6 w-6" />
          </div>
          <div>
            <h2 className="font-display text-2xl font-black">{t(`costs.groups.${group.key}.label`)}</h2>
            <p className="text-xs text-[color:var(--sp-text-muted)] mt-1">{t(`costs.groups.${group.key}.hint`)}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--sp-text-muted)]">{t("costs.totalPeriod")}</div>
            <div className="font-display text-2xl font-black" style={{ color: group.color }}>{fmtMoney(total)}</div>
          </div>
          <button onClick={onAdd} data-testid={`cost-add-in-${group.key}`}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold text-white transition hover:brightness-110"
                  style={{ background: group.color }}>
            <Plus className="h-3.5 w-3.5" /> {t("common.add")}
          </button>
        </div>
      </header>

      {/* Sub-category breakdown */}
      {subEntries.length > 0 && (
        <div className="px-6 py-4 border-t border-[color:var(--sp-border)] space-y-2">
          {subEntries.map(([sub, val]) => {
            const pct = total ? (val / total) * 100 : 0;
            return (
              <div key={sub}>
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold">{subLabel(sub)}</span>
                  <span className="font-black">
                    {fmtMoney(val)}
                    <span className="text-[color:var(--sp-text-muted)] text-[10px] ml-2">{pct.toFixed(0)}%</span>
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-[color:var(--sp-subtle)] mt-1 overflow-hidden">
                  <div className="h-full transition-all" style={{ width: `${pct}%`, background: group.color }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Expandable item list */}
      <div className="border-t border-[color:var(--sp-border)]">
        <button onClick={onToggleExpand}
                className="w-full px-6 py-3 flex items-center justify-between text-sm hover:bg-[color:var(--sp-subtle)] transition"
                data-testid={`cost-toggle-list-${group.key}`}>
          <span className="font-bold inline-flex items-center gap-2">
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            {t("costs.detail.registeredItems", { n: items.length })}
          </span>
          <span className="text-xs text-[color:var(--sp-text-muted)]">
            {items.length === 0
              ? t("costs.detail.noItemsPeriod")
              : `${expanded ? t("costs.detail.hide") : t("costs.detail.show")} ${t("costs.detail.list")}`}
          </span>
        </button>
        {expanded && (
          <div className="overflow-hidden">
            {items.length === 0 ? (
              <div className="p-8 text-center text-sm text-[color:var(--sp-text-muted)]">
                {t("costs.detail.noCosts")}
              </div>
            ) : (
              <div className="sp-table-wrap">
              <table className="w-full">
                <thead className="bg-[color:var(--sp-subtle)] text-left">
                  <tr className="label-eyebrow">
                    <th className="p-4">{t("costs.detail.cols.description")}</th>
                    <th className="p-4">{t("costs.detail.cols.subcat")}</th>
                    <th className="p-4">{t("costs.detail.cols.date")}</th>
                    <th className="p-4">{t("costs.detail.cols.recurrence")}</th>
                    <th className="p-4 text-right">{t("costs.detail.cols.amount")}</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((c, i) => (
                    <tr key={c.id} data-testid={`cost-row-${c.id}`} className={i % 2 ? "bg-[color:var(--sp-bg)]" : ""}>
                      <td className="p-4">
                        <button onClick={() => onEdit(c)} className="font-bold hover:text-[color:var(--sp-brand)] text-left">{c.name}</button>
                        {c.notes && <div className="text-xs text-[color:var(--sp-text-muted)] mt-0.5 line-clamp-1">{c.notes}</div>}
                      </td>
                      <td className="p-4 text-xs text-[color:var(--sp-text-secondary)]">{subLabel(c.subcategory) || "—"}</td>
                      <td className="p-4 text-sm text-[color:var(--sp-text-secondary)]">{fmtDate(c.date)}</td>
                      <td className="p-4 text-sm">
                        {c.recurring ? (
                          <span className="inline-flex items-center gap-1 text-xs font-bold text-[color:var(--sp-brand)]">
                            <Repeat className="h-3 w-3" /> {c.frequency === "yearly" ? t("costs.form.yearly") : t("costs.form.monthly")}
                          </span>
                        ) : (
                          <span className="text-[color:var(--sp-text-muted)] text-xs">{t("costs.detail.oneOff")}</span>
                        )}
                      </td>
                      <td className="p-4 text-right font-display font-black text-[color:var(--sp-danger)]">
                        - {fmtMoney(c.amount || 0)}
                      </td>
                      <td className="p-4 text-right">
                        <div className="inline-flex gap-1">
                          <button data-testid={`cost-edit-${c.id}`} onClick={() => onEdit(c)} className="p-1 text-[color:var(--sp-text-secondary)] hover:text-[color:var(--sp-brand)]">
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button data-testid={`cost-delete-${c.id}`} onClick={() => onDelete(c.id)} className="p-1 text-[color:var(--sp-text-secondary)] hover:text-[color:var(--sp-danger)]">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
